import { Box, Card, CardContent, Typography } from "@mui/material"
import { useSession } from "@utils/auth"
import type { ReactNode } from "react"
import { useEffect, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { CliAuthConsent } from "./cli-auth-consent"
import type { CliAuthRequest } from "./constants"
import { storeCliAuthRequest } from "./constants"
import { resolveCliAuthRequest } from "./helpers"

const CliAuthLayout = ({ children }: { children: ReactNode }) => (
	<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", p: 2 }}>
		<Card sx={{ width: "100%", maxWidth: 480 }}>
			<CardContent>{children}</CardContent>
		</Card>
	</Box>
)

/**
 * Entry point for the local-tool ("CLI") login flow.
 *
 * The request comes in via `redirect_uri` + `state` query params. Because the user is bounced
 * through /login (which loses the query), the request is also read back from sessionStorage when
 * the query is absent.
 *
 * - Not signed in → redirect to /login, which sends the user back here after the sign-in.
 * - Signed in → render the consent screen. The token is only handed back on explicit approval.
 */
function CliAuth() {
	const session = useSession()
	const location = useLocation()
	const navigate = useNavigate()

	const request = useMemo<CliAuthRequest | null>(() => resolveCliAuthRequest(location.search), [location.search])

	// Persist before we navigate away: the request must outlive the /login step. This effect is
	// declared before the redirect one so the request is always stored first.
	useEffect(() => {
		if (request) storeCliAuthRequest(request)
	}, [request])

	useEffect(() => {
		if (request && !session) navigate("/login", { state: { from: location }, replace: true })
	}, [request, session, location, navigate])

	if (!request) {
		return (
			<CliAuthLayout>
				<Typography variant="h5" gutterBottom>
					Invalid sign-in request
				</Typography>
				<Typography>
					This sign-in link is missing or has an invalid callback address. Please start the sign-in again from your tool.
				</Typography>
			</CliAuthLayout>
		)
	}

	// The redirect to /login runs in the effect above; render the empty card until it happens.
	if (!session) return <CliAuthLayout>{null}</CliAuthLayout>

	return (
		<CliAuthLayout>
			<CliAuthConsent request={request} accessToken={session.accessToken} email={session.data.user.email} />
		</CliAuthLayout>
	)
}

export default CliAuth
